import { type Graphics, Rectangle } from "pixi.js";
import { memo, useCallback, useMemo } from "react";
import { useMapObjectInteractions } from "../hooks/useMapObjectInteractions";
import { useScene } from "../hooks/useScene";
import { ACTIVE_INTERACTION_COLOR } from "../interaction";
import type { FlyzoneMapObject, LayerId, MapItemHandler } from "../types";

const FLYABLE_COLOR = 0x00ff00;
const FLYABLE_ALPHA = 0.15;
const NON_FLYABLE_COLOR = 0xff0000;
const NON_FLYABLE_ALPHA = 0.5;
const HOVER_OUTLINE_WIDTH_WORLD = 0.05;

interface FlyzoneObjectProps {
    item: FlyzoneMapObject;
    layerId: LayerId;
    selected: boolean;
    onItemClick?: MapItemHandler<FlyzoneMapObject>;
    onItemHover?: MapItemHandler<FlyzoneMapObject>;
    onItemLeave?: MapItemHandler<FlyzoneMapObject>;
}

function useFlyzoneGeometry(item: FlyzoneMapObject) {
    const { coordinates } = useScene();
    const flyzoneScene = useMemo(
        () =>
            coordinates.worldRectToScene({
                x: item.x,
                y: item.y,
                width: item.width,
                height: item.height,
            }),
        [coordinates, item.x, item.y, item.width, item.height],
    );
    const bounds = useMemo(
        () =>
            new Rectangle(
                flyzoneScene.x,
                flyzoneScene.y,
                flyzoneScene.width,
                flyzoneScene.height,
            ),
        [flyzoneScene],
    );
    const hoverOutlineWidth = coordinates.worldLengthToScene(
        HOVER_OUTLINE_WIDTH_WORLD,
    );

    return { flyzoneScene, bounds, hoverOutlineWidth };
}

const FlyzoneObject = ({
    item,
    layerId,
    selected,
    onItemClick,
    onItemHover,
    onItemLeave,
}: FlyzoneObjectProps) => {
    const { flyzoneScene, bounds, hoverOutlineWidth } =
        useFlyzoneGeometry(item);
    const interaction = useMapObjectInteractions({
        item,
        layerId,
        onItemClick,
        onItemHover,
        onItemLeave,
    });
    const active = interaction.hovered || selected;

    const draw = useCallback(
        (graphics: Graphics) => {
            graphics.clear();
            graphics.rect(
                flyzoneScene.x,
                flyzoneScene.y,
                flyzoneScene.width,
                flyzoneScene.height,
            );
            graphics.fill({
                color: item.flyable ? FLYABLE_COLOR : NON_FLYABLE_COLOR,
                alpha: item.flyable ? FLYABLE_ALPHA : NON_FLYABLE_ALPHA,
            });

            if (active) {
                graphics.stroke({
                    color: ACTIVE_INTERACTION_COLOR,
                    width: hoverOutlineWidth,
                });
            }
        },
        [active, flyzoneScene, hoverOutlineWidth, item.flyable],
    );

    return (
        <pixiContainer
            label="flyzone-culling-unit"
            cullable
            cullableChildren={false}
            cullArea={bounds}
        >
            <pixiGraphics
                eventMode={interaction.eventMode}
                cursor={interaction.cursor}
                hitArea={bounds}
                onPointerOver={interaction.onPointerOver}
                onPointerOut={interaction.onPointerOut}
                onPointerTap={interaction.onPointerTap}
                draw={draw}
            />
        </pixiContainer>
    );
};

export default memo(FlyzoneObject, (prev, next) => {
    return (
        prev.item === next.item &&
        prev.layerId === next.layerId &&
        prev.selected === next.selected &&
        prev.onItemClick === next.onItemClick &&
        prev.onItemHover === next.onItemHover &&
        prev.onItemLeave === next.onItemLeave
    );
});
